import { useContext, useState, useEffect } from "react";
import { PhoneInput } from 'react-international-phone';
import AuthContext from "../context/auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import useTranslator from "../hooks/useTranslator";
import { Modal } from "react-bootstrap";
import Canceling from "../assets/img/cancel-01.svg";
import { toast } from "../helper/common";

const EditProfileModal = ({ show, onClose }) => {
  const { _t } = useTranslator();

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { user, updateProfile } = authContext;
  const { workspaceId } = useParams();

  const [phoneNumber, setPhoneNumber] = useState();
  const [errors, setErrors] = useState({});

  const [LandlordProfile, setLandlordProfile] = useState({
    fname_companyName: "",
    lname: "",
    cif_dni_nie: "",
    phone: "",
    linkedin_profile: "",
    email: "",
    type: "landlord",
    is_edit: true,
  });

  // ✅ VALIDATE FIELD
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "fname_companyName":
        if (!value.trim()) error = _t("firstNameIsRequired");
        else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(value))
          error = _t("firstNameInvalid");
        break;

      case "lname":
        if (!value.trim()) error = _t("lastNameIsRequired");
        else if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(value))
          error = _t("lnameNameInvalid");
        break;

      case "email":
        if (!value.trim()) error = _t("emailIsRequired");
        else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value.trim())
        )
          error = _t("invalidEmailFormat");
        break;

      case "phone":
        if (!value || value.length <= 5)
          error = _t("phoneNumberMustBeAtLeastSixDigits");
        break;

      default:
        break;
    }

    return error;
  };

  // ✅ HANDLE INPUT CHANGE + VALIDATE
  const onChangeLandlord = (e) => {
    const { name, value } = e.target;

    setLandlordProfile((prev) => ({
      ...prev,
      [name]: value,
    }));

    // validate + update error
    const error = validateField(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // ✅ PHONE CHANGE + VALIDATE
  const onPhoneChange = (value) => {
    const phone = value?.replace("+", "") || "";

    setPhoneNumber(phone);

    setLandlordProfile((prev) => ({
      ...prev,
      phone: phone,
    }));

    // const error = validateField("phone", phone);

    // setErrors((prev) => ({
    //   ...prev,
    //   phone: error,
    // }));
  };

  // ✅ FORM VALIDATION
  const isFormValid = () => {
    const newErrors = {};

    const fields = [
      { key: "fname_companyName", value: LandlordProfile.fname_companyName },
      { key: "lname", value: LandlordProfile.lname },
      { key: "email", value: LandlordProfile.email },
      { key: "phone", value: LandlordProfile.phone },
    ];

    fields.forEach(({ key, value }) => {
      const err = validateField(key, value);
      if (err) newErrors[key] = err;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ SUBMIT
  const onSubmitLandlord = (e) => {
    e.preventDefault();

    if (!isFormValid()) return;

    updateProfile(user?._id, {
      ...LandlordProfile,
      role: "landlord",
      is_edit: true,
    });

    onClose();
  };

  const closeButton = () => {
    navigate(`/${workspaceId}/landlord-profile`);
  };

  useEffect(() => {
    if(LandlordProfile?.email){
      return;
    }
    if (user) setLandlordProfile(user);
  }, [user]);

  useEffect(() => {
    setPhoneNumber(LandlordProfile?.phone || "");
  }, [LandlordProfile]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="landlord-editProfile-modal"
    >
      <Modal.Header>
        <Modal.Title>{_t("editProfile")}</Modal.Title>
        <div className="cursor-pointer" onClick={onClose}>
          <img src={Canceling} alt="icon" className="canceicon topRightIcon" />
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={onSubmitLandlord} className="edit-profile-form">
          <div className="form-grid">
            {/* FIRST NAME */}
            <div className="custom-col-3">
              <div
                className={`forminput customInput mb-0 ${
                  errors.fname_companyName ? "form-group-error" : ""
                }`}
              >
                <label>{_t("firstName")}</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder={_t("enterFirstName")}
                  name="fname_companyName"
                  value={LandlordProfile?.fname_companyName || ""}
                  onChange={onChangeLandlord}
                  onBeforeInput={(e) => {
                      const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                      if (!isValidChar) e.preventDefault();
                    }}
                />
              </div>
              <p className="error-message">
                {errors.fname_companyName}
              </p>
            </div>

            {/* LAST NAME */}
            <div className="custom-col-3">
              <div
                className={`forminput customInput mb-0 ${
                  errors.lname ? "form-group-error" : ""
                }`}
              >
                <label>{_t("lastName")}</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder={_t("enterLastName")}
                  name="lname"
                  value={LandlordProfile?.lname || ""}
                  onChange={onChangeLandlord}
                  onBeforeInput={(e) => {
                      const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                      if (!isValidChar) e.preventDefault();
                    }}
                />
              </div>
              <p className="error-message">
                {errors.lname}
              </p>
            </div>

            {/* PHONE */}
            <div className="custom-col-6">
              <div
                className={`forminput customInput mb-0 ${
                  errors.phone ? "form-group-error" : ""
                }`}
              >
                <label>{_t("phoneNumber")}</label>
                <PhoneInput
                  className="phone_input_info"
                  international
                  defaultCountry={"es"}
                  value={phoneNumber}
                  onChange={onPhoneChange}
                />
              </div>
              <p className="error-message">
                {errors.phone}
              </p>
            </div>

            {/* LINKEDIN */}
            <div className="custom-col-6">
              <div className="forminput customInput mb-0">
                <label>{_t("linkedInProfile")}</label>
                <input
                  type="text"
                  className="modal-input"
                  name="linkedin_profile"
                  placeholder={_t("enterLinkedInProfile")}
                  value={LandlordProfile?.linkedin_profile || ""}
                  onChange={onChangeLandlord}
                />
              </div>
            </div>

            {/* EMAIL */}
            <div className="custom-col-6">
              <div
                className={`forminput customInput mb-0 ${
                  errors.email ? "form-group-error" : ""
                }`}
              >
                <label>{_t("email")}</label>
                <input
                  type="text"
                  className="modal-input"
                  name="email"
                  placeholder={_t("enterEmailAddress", "LL edit profile")}
                  value={LandlordProfile?.email || ""}
                  onChange={onChangeLandlord}
                />
              </div>
              <p className="error-message">
                {errors.email}
              </p>
            </div>
          </div>
        </form>

        <div className="edit-profile-btn">
          <button onClick={onSubmitLandlord}>
            {_t("saveChanges")}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EditProfileModal;